'use client';

import Link from 'next/link';
import { CalendarX } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

/**
 * Not-found boundary for a single event -- what `notFound()` in this segment's
 * page.tsx renders (the only notFound() call in the app).
 *
 * Kept resource-specific rather than leaning on the root boundary because the
 * reader arrived from an event link and the calendar is the one place that can
 * still help them: React Standards 11.3, "Resource-specific not-found pages".
 *
 * Structure deliberately mirrors the sibling dead-end page
 * `groups/[documentId]/join-to-view/page.tsx` -- same card, same icon bubble,
 * same primary/secondary action pair -- so the two read as one family.
 *
 * No retry control, for the reason given in the root boundary.
 */
export default function EventNotFound() {
  const { t } = useLanguage();

  return (
    <div className="max-w-2xl mx-auto px-3 md:px-6 py-16">
      <div
        data-testid="event-not-found-card"
        className="rounded-2xl border border-border bg-background-secondary p-8 text-center shadow-sm space-y-4"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CalendarX size={24} aria-hidden="true" />
        </div>
        <h1 data-testid="event-not-found-title" className="text-2xl font-semibold text-foreground">
          {t.eventDetail.eventNotFound}
        </h1>
        <p data-testid="event-not-found-description" className="text-sm text-foreground-muted">
          {t.eventDetail.eventNotFoundDescription}
        </p>

        <div className="flex flex-col sm:flex-row sm:justify-center gap-3 pt-2">
          <Link
            href="/events"
            data-testid="event-not-found-events-link"
            className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white hover:opacity-90 transition"
          >
            {t.eventDetail.backToEvents}
          </Link>
          <Link
            href="/"
            data-testid="event-not-found-home-link"
            className="inline-flex items-center justify-center rounded-md border border-border px-5 py-2.5 text-sm font-medium text-foreground hover:bg-background transition"
          >
            {t.notFoundPage.goHome}
          </Link>
        </div>
      </div>
    </div>
  );
}
